import { CalendarDate } from '@internationalized/date';
import { Money, PaymentTerm } from '@rematter/frontend-lib-api-client';
import { atom, Getter } from 'jotai';

import { invoiceMarkAsPaidAtom } from '../_graphql/mutationInvoiceMarkAsPaid';
import { invoiceUpdateAtom } from '../_graphql/mutationInvoiceUpdate';
import { invoiceVoidAtom } from '../_graphql/mutationInvoiceVoid';
import { invoiceDetailQueryAtom } from '../_graphql/queryInvoiceDetail';
import { financeRoutes } from '../routes';
import {
  InvoiceDetailsTab,
  InvoiceLineItemRecord,
  InvoiceRecord,
} from '../types';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

export type DraftInvoiceInfo = {
  documentDate: CalendarDate | null;
  documentNumber: string | null;
  dueDate: CalendarDate | null;
  paymentTerm: PaymentTerm | null;
  notes: string | null;
  amount: Money | null;
  facilityId: string | null;
};

// 🟣🟣🟣 PARAMS - Invoice Detail View
export const $invoiceDetailPathParams = atomFromRoutePath(
  financeRoutes.invoiceDetail,
);
export const $invoiceId = atom((get) =>
  get($invoiceDetailPathParams)?.id
    ? String(get($invoiceDetailPathParams)?.id)
    : null,
);

// 🟢🟢🟢 REMOTE DATA - Invoice Detail
export const $remoteInvoiceDetail = invoiceDetailQueryAtom((get) => ({
  variables: { id: get($invoiceId) ?? '' },
  skip: !get($invoiceId),
}));

const refetchInvoiceDetail = (get: Getter) => {
  const invoiceId = get($invoiceId);
  if (!invoiceId) {
    return;
  }
  get($remoteInvoiceDetail).refetch?.();
};

// 🟠🟠🟠 ACTIONS - Invoice Update
export const $updateInvoice = invoiceUpdateAtom({
  onSuccess: (get, set) => {
    set($currentDraftLineItems, null);
    set($currentDraftInvoiceInfo, null);
    refetchInvoiceDetail(get);
    set($syncDraftWithRemote, true);
  },
});

// 🟠🟠🟠 ACTIONS - Invoice Void
export const $voidInvoice = invoiceVoidAtom({
  onSuccess: (get) => {
    refetchInvoiceDetail(get);
  },
});

// 🟠🟠🟠 ACTIONS - Invoice Mark As Paid
export const $markInvoiceAsPaid = invoiceMarkAsPaidAtom({
  onSuccess: (get) => {
    refetchInvoiceDetail(get);
  },
});

// 🔘🔘🔘 PLAIN ATOMS - UI State
export const $tab = atomFromQueryString.string<InvoiceDetailsTab>('tab');
export const $isEditMode = atom<boolean>(false);
export const $hasUnsavedChanges = atom<boolean>(false);

export const $isInvoiceLoading = atom((get) => {
  const data = get($remoteInvoiceDetail);
  return data?.isLoading ?? false;
});

export const $invoiceError = atom((get) => {
  const data = get($remoteInvoiceDetail);
  return data?.error ?? null;
});

// 🟡🟡🟡 DRAFT STATE - Line Items Draft Changes
export const $draftLineItems = atom<Map<string, InvoiceLineItemRecord[]>>(
  new Map(),
);

export const $currentDraftLineItems = atom(
  (get) => {
    const invoiceId = get($invoiceId);
    const draftMap = get($draftLineItems);
    return invoiceId ? (draftMap.get(invoiceId) ?? null) : null;
  },
  (get, set, lineItems: InvoiceLineItemRecord[] | null) => {
    const invoiceId = get($invoiceId);
    if (!invoiceId) {
      return;
    }

    set($draftLineItems, (prev) => {
      const newMap = new Map(prev);
      if (lineItems === null) {
        newMap.delete(invoiceId);
      } else {
        newMap.set(invoiceId, lineItems);
      }
      return newMap;
    });
  },
);

export const $effectiveLineItems = atom((get) => {
  const result = get($remoteInvoiceDetail);
  const draftLineItems = get($currentDraftLineItems);
  const remoteLineItems = result?.data?.invoice?.lineItems ?? [];

  if (draftLineItems !== null) {
    return draftLineItems;
  }

  return remoteLineItems;
});

export const $lastSavedLineItemsTimestamp = atom<string | null>(null);

export const $syncDraftWithRemote = atom(
  (get) => {
    const result = get($remoteInvoiceDetail);
    const lastTimestamp = get($lastSavedLineItemsTimestamp);

    if (result?.data?.invoice?.timestamps?.updatedAt) {
      return (
        result.data.invoice.timestamps.updatedAt.toString() !== lastTimestamp
      );
    }
    return false;
  },
  (get, set, shouldClearDraft = false) => {
    const result = get($remoteInvoiceDetail);
    const currentDraft = get($currentDraftLineItems);

    if (result?.data?.invoice?.timestamps?.updatedAt) {
      const currentTimestamp =
        result.data.invoice.timestamps.updatedAt.toString();
      set($lastSavedLineItemsTimestamp, currentTimestamp);

      if (shouldClearDraft && currentDraft !== null) {
        set($currentDraftLineItems, null);
      }
    }
  },
);

export const $hasDraftChanges = atom((get) => {
  const result = get($remoteInvoiceDetail);
  const draftLineItems = get($currentDraftLineItems);

  if (!result?.data?.invoice || draftLineItems === null) {
    return false;
  }

  const originalLineItems = result.data.invoice.lineItems ?? [];

  if (originalLineItems.length !== draftLineItems.length) {
    return true;
  }

  return draftLineItems.some((draftItem, index) => {
    const originalItem = originalLineItems[index];
    if (!originalItem) return true;

    return (
      draftItem.description !== originalItem.description ||
      draftItem.quantity !== originalItem.quantity ||
      draftItem.unit !== originalItem.unit ||
      draftItem.unitPrice?.amount !== originalItem.unitPrice?.amount ||
      draftItem.unitPrice?.currencyCode !== originalItem.unitPrice?.currencyCode
    );
  });
});

export const $clearCurrentDraftLineItems = atom(null, (get, set) => {
  set($currentDraftLineItems, null);
});

export const $draftInvoiceInfo = atom<Map<string, DraftInvoiceInfo>>(new Map());

export const $currentDraftInvoiceInfo = atom(
  (get) => {
    const invoiceId = get($invoiceId);
    const draftMap = get($draftInvoiceInfo);
    return invoiceId ? (draftMap.get(invoiceId) ?? null) : null;
  },
  (get, set, info: DraftInvoiceInfo | null) => {
    const invoiceId = get($invoiceId);
    if (!invoiceId) {
      return;
    }

    set($draftInvoiceInfo, (prev) => {
      const newMap = new Map(prev);
      if (info === null) {
        newMap.delete(invoiceId);
      } else {
        newMap.set(invoiceId, info);
      }
      return newMap;
    });
  },
);

export const $effectiveInvoiceInfo = atom((get) => {
  const result = get($remoteInvoiceDetail);
  const draftInfo = get($currentDraftInvoiceInfo);
  const remoteInvoice = result?.data?.invoice;

  if (!remoteInvoice) {
    return null;
  }

  if (draftInfo !== null) {
    return {
      ...remoteInvoice,
      documentDate: draftInfo.documentDate,
      documentNumber: draftInfo.documentNumber,
      dueDate: draftInfo.dueDate,
      paymentTerm: draftInfo.paymentTerm,
      notes: draftInfo.notes,
      amount: draftInfo.amount ?? remoteInvoice.amount,
      facilityId: draftInfo.facilityId ?? remoteInvoice.facilityId,
    };
  }

  return remoteInvoice;
});

export const $hasDraftInfoChanges = atom((get) => {
  const result = get($remoteInvoiceDetail);
  const draftInfo = get($currentDraftInvoiceInfo);

  if (!result?.data?.invoice || draftInfo === null) {
    return false;
  }

  const invoice = result.data.invoice;

  const documentDateChanged =
    (draftInfo.documentDate?.toString() ?? null) !==
    (invoice.documentDate?.toString() ?? null);
  const documentNumberChanged =
    draftInfo.documentNumber !== invoice.documentNumber;
  const dueDateChanged =
    (draftInfo.dueDate?.toString() ?? null) !==
    (invoice.dueDate?.toString() ?? null);
  const paymentTermChanged = draftInfo.paymentTerm !== invoice.paymentTerm;
  const notesChanged = draftInfo.notes !== invoice.notes;
  const amountChanged =
    draftInfo.amount?.amount !== invoice.amount?.amount ||
    draftInfo.amount?.currencyCode !== invoice.amount?.currencyCode;
  const facilityIdChanged = draftInfo.facilityId !== invoice.facilityId;

  return (
    documentDateChanged ||
    documentNumberChanged ||
    dueDateChanged ||
    paymentTermChanged ||
    notesChanged ||
    amountChanged ||
    facilityIdChanged
  );
});

export const $isDirty = atom((get) => {
  const hasDraftLineItemChanges = get($hasDraftChanges);
  const hasDraftInfoChanges = get($hasDraftInfoChanges);
  return hasDraftLineItemChanges || hasDraftInfoChanges;
});

export const $discardAllDrafts = atom(null, (get, set) => {
  set($currentDraftLineItems, null);
  set($currentDraftInvoiceInfo, null);
});

export const $initDraftFromInvoice = atom(
  null,
  (get, set, invoice: InvoiceRecord) => {
    set($currentDraftInvoiceInfo, {
      documentDate: invoice.documentDate,
      documentNumber: invoice.documentNumber,
      dueDate: invoice.dueDate,
      paymentTerm: invoice.paymentTerm,
      notes: invoice.notes,
      amount: invoice.amount,
      facilityId: invoice.facilityId,
    });
  },
);
